/**
 * The customer's data, taken away (PRD 13.4): the people utuwrk knows about,
 * every card of every onboarding and offboarding, and the organisation's own
 * templates. The audit log has its own export beside its search (audit.ts).
 *
 * Exports are for HR, admins and compliance, and every one is itself a row in
 * the log. Private values stay out of them -- a private field reads "[PII]" --
 * unless a compliance officer asks for them, and that export is also logged as
 * a view of private values.
 */

import { csvCell } from "./audit.js";
import type { EmployeeSummary } from "./api.js";
import type { Actor } from "./engine.js";
import { isRedacted } from "./erasure.js";
import type { Instance } from "./instance.js";
import type { LifecycleKind, Template } from "./template.js";

export const DATA_EXPORTS = {
  employees: "Employees",
  work: "Work history",
  templates: "Templates",
} as const;

export type DataExport = keyof typeof DATA_EXPORTS;

export const isDataExport = (value: unknown): value is DataExport =>
  typeof value === "string" && value in DATA_EXPORTS;

/** HR, admins and compliance take the organisation's data away (PRD 13.4). */
export const canExportData = (actor: Pick<Actor, "role">): boolean =>
  actor.role === "admin" || actor.role === "hr" || actor.role === "compliance";

/** Private values leave in an export only on a compliance officer's say (PRD 13.4). */
export const canExportPrivate = (actor: Pick<Actor, "role">): boolean => actor.role === "compliance";

/** Rows as CSV, header first, lines ended the way spreadsheets expect. */
export function csv(
  header: readonly string[],
  rows: readonly (readonly (string | number | null)[])[],
): string {
  return `${[header.join(","), ...rows.map((row) => row.map(csvCell).join(","))].join("\r\n")}\r\n`;
}

const iso = (ms: number | null): string | null => (ms === null ? null : new Date(ms).toISOString());

/** Everybody utuwrk has onboarded or offboarded, as the Employees page lists them. Nothing private. */
export function employeesCsv(rows: readonly EmployeeSummary[]): string {
  return csv(
    [
      "employee_id",
      "name",
      "state",
      "site",
      "department",
      "employee_type",
      "employment_status",
      "manager",
      "start_date",
      "contract_ends",
      "last_day",
      "open_cards",
    ],
    rows.map((row) => [
      row.id,
      row.name,
      row.state,
      row.siteName,
      row.department,
      row.employeeType,
      row.employmentStatus,
      row.managerName,
      row.startDate,
      row.endDate,
      row.lastDay,
      row.openCards,
    ]),
  );
}

/** One onboarding, offboarding or access change, with what the work export needs beside the instance. */
export interface WorkExportItem {
  id: string;
  kind: LifecycleKind;
  templateName: string;
  employeeName: string;
  siteName: string;
  createdAt: number;
  closedAt: number | null;
  instance: Instance;
}

/**
 * Every card of every piece of work, one row each (PRD 13.4's "task-level
 * completion data"). A card's recorded values are one cell, a line each;
 * private values read "[PII]" unless `withPrivate`, and the count of those
 * written is returned so the export can be logged as a view of them.
 */
export function workCsv(
  items: readonly WorkExportItem[],
  nameOf: (id: string | null) => string | null,
  withPrivate: boolean,
): { text: string; rows: number; privateValues: number } {
  let privateValues = 0;
  const rows: (string | number | null)[][] = [];
  for (const item of items) {
    const { instance } = item;
    for (const key of instance.order) {
      const node = instance.nodes[key];
      const task = instance.tasks[key];
      if (!node || !task) continue;
      const values = (node.fields ?? [])
        .filter((field) => task.values[field.key])
        .map((field) => {
          const value = task.values[field.key]!;
          if (!field.pii || isRedacted(value)) return `${field.label}: ${value}`;
          if (!withPrivate) return `${field.label}: [PII]`;
          privateValues += 1;
          return `${field.label}: ${value}`;
        });
      rows.push([
        item.id,
        item.kind,
        item.templateName,
        instance.templateVersion,
        item.employeeName,
        item.siteName,
        instance.anchorDate,
        iso(item.createdAt),
        iso(item.closedAt),
        key,
        node.title,
        task.department,
        task.state,
        task.dueDay,
        iso(task.startedAt),
        iso(task.finishedAt),
        nameOf(task.finishedBy),
        task.skipReason,
        values.join("\n") || null,
      ]);
    }
  }
  const text = csv(
    [
      "workflow_id",
      "workflow",
      "template",
      "template_version",
      "employee",
      "site",
      "start_or_last_day",
      "started_utc",
      "closed_utc",
      "card_key",
      "card",
      "department",
      "state",
      "due_day",
      "card_started_utc",
      "card_finished_utc",
      "finished_by",
      "skip_reason",
      "recorded",
    ],
    rows,
  );
  return { text, rows: rows.length, privateValues };
}

export interface TemplateExportItem {
  id: string;
  name: string;
  retired: boolean;
  /** Oldest first. */
  versions: { version: number; savedAt: number; savedBy: string; template: Template }[];
}

/** The organisation's own templates, every saved version, as readable JSON (PRD 13.4). The starters are utuwrk's. */
export function templatesJson(items: readonly TemplateExportItem[], exportedAt: number): string {
  return `${JSON.stringify(
    {
      exportedAt: new Date(exportedAt).toISOString(),
      templates: items.map((item) => ({
        id: item.id,
        name: item.name,
        retired: item.retired,
        versions: item.versions.map((version) => ({
          version: version.version,
          savedAt: new Date(version.savedAt).toISOString(),
          savedBy: version.savedBy,
          template: version.template,
        })),
      })),
    },
    null,
    2,
  )}\n`;
}
